import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { clearToken } from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import BrandFooter from "../components/BrandFooter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Loader2, Search, RefreshCw, ExternalLink, Eye, Pencil, Building2, Cloud, Mail, MailX, FileText, Database, Users as UsersIcon, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

const SOURCE_STYLES = {
  onboarding:    { label: "Onboarding",    cls: "bg-amber-50 text-amber-700 border-amber-200" },
  itglue_import: { label: "ITGlue import", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  both:          { label: "Both",          cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const STATUS_STYLES = {
  not_started: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  submitted:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  imported:    "bg-sky-50 text-sky-700 border-sky-200",
  reified:     "bg-violet-50 text-violet-700 border-violet-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
}

export default function AdminClients() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState({ key: "last_activity_at", dir: "desc" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/clients");
      setRows(data.items || []);
      setStats(data.stats || null);
    } catch (e) {
      if (e?.response?.status === 401) { clearToken(); nav("/login"); return; }
      toast.error("Failed to load clients");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = rows.filter(r => {
      if (sourceFilter !== "all" && r.source !== sourceFilter) return false;
      if (!term) return true;
      return (
        (r.company_name || "").toLowerCase().includes(term) ||
        (r.contact_email || "").toLowerCase().includes(term) ||
        (r.contact_name || "").toLowerCase().includes(term) ||
        String(r.itglue_org_id || "").includes(term)
      );
    });
    const { key, dir } = sortBy;
    out = [...out].sort((a, b) => {
      const av = a[key] ?? "";
      const bv = b[key] ?? "";
      const cmp = typeof av === "number" ? (av - bv) : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, q, sourceFilter, sortBy]);

  const toggleSort = (key) => {
    setSortBy(cur => cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col" data-testid="admin-clients-page">
      <BrandHeader showAdmin showAuditLog showSecurityEvents showImport showSettings/>
      <main className="max-w-7xl mx-auto w-full px-6 py-8 flex-1">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-brand-primary flex items-center gap-2">
              <UsersIcon size={22}/> Clients
            </h1>
            <p className="text-sm text-slate-600 mt-1">Every client we have data about — from onboardings and ITGlue imports.</p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-clients-btn" className="gap-2">
            {loading ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
            Refresh
          </Button>
        </div>

        {/* Stats row */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="clients-stats">
            <StatCard label="Total" value={stats.total} testId="stat-total" />
            <StatCard label="Onboarding only" value={stats.onboarding_only} tone="amber" testId="stat-onboarding" />
            <StatCard label="Import only" value={stats.import_only} tone="sky" testId="stat-import" />
            <StatCard label="Both" value={stats.both} tone="emerald" testId="stat-both" />
            <StatCard label="In ITGlue" value={stats.with_itglue} tone="violet" testId="stat-itglue" />
          </div>
        )}

        {/* Filters */}
        <Card className="p-4 mb-4 bg-white border-slate-200">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <Input
                placeholder="Search company, email, or ITGlue org id"
                value={q}
                onChange={e => setQ(e.target.value)}
                className="pl-9"
                data-testid="clients-search-input"
              />
            </div>
            <div className="flex gap-1 p-1 bg-slate-100 rounded-md text-sm" data-testid="clients-source-tabs">
              {[
                { k: "all", label: "All" },
                { k: "onboarding", label: "Onboarding" },
                { k: "itglue_import", label: "Import" },
                { k: "both", label: "Both" },
              ].map(t => (
                <button
                  key={t.k}
                  onClick={() => setSourceFilter(t.k)}
                  data-testid={`filter-${t.k}`}
                  className={`px-3 py-1.5 rounded transition ${sourceFilter === t.k ? "bg-white shadow-sm font-medium text-brand-primary" : "text-slate-600 hover:text-slate-900"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="bg-white border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <tr>
                  <SortHeader label="Company" onClick={() => toggleSort("company_name")} active={sortBy.key === "company_name"} dir={sortBy.dir} testId="sort-company"/>
                  <th className="px-4 py-2 text-left font-medium">Source</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Contact</th>
                  <SortHeader label="Contacts" onClick={() => toggleSort("contact_count")} active={sortBy.key === "contact_count"} dir={sortBy.dir} testId="sort-contacts" align="right"/>
                  <SortHeader label="Progress" onClick={() => toggleSort("section_progress_pct")} active={sortBy.key === "section_progress_pct"} dir={sortBy.dir} testId="sort-progress" align="right"/>
                  <th className="px-4 py-2 text-left font-medium">Where data lives</th>
                  <SortHeader label="Last activity" onClick={() => toggleSort("last_activity_at")} active={sortBy.key === "last_activity_at"} dir={sortBy.dir} testId="sort-activity"/>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={9} className="text-center py-8 text-slate-500" data-testid="clients-loading"><Loader2 className="animate-spin inline mr-2" size={16}/>Loading clients…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-8 text-slate-500" data-testid="clients-empty">No clients match this filter.</td></tr>
                )}
                {filtered.map(r => {
                  const src = SOURCE_STYLES[r.source] || SOURCE_STYLES.onboarding;
                  const statusCls = STATUS_STYLES[r.status] || "bg-slate-100 text-slate-700 border-slate-200";
                  return (
                    <tr key={r.id} data-testid={`client-row-${r.id}`} className="hover:bg-slate-50/60 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 flex items-center gap-2">
                          <Building2 size={14} className="text-slate-400 shrink-0"/>
                          <span className="truncate max-w-[260px]" title={r.company_name}>{r.company_name}</span>
                          {r.notifications_silenced && (
                            <span title="Notifications silenced (client blocklisted)" data-testid={`silenced-${r.id}`}>
                              <MailX size={14} className="text-rose-500"/>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${src.cls}`} data-testid={`source-${r.id}`}>
                          {src.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${statusCls}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-800">{r.contact_name || <span className="text-slate-400">—</span>}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[220px]" title={r.contact_email}>{r.contact_email || <span className="text-slate-300">—</span>}</div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <UsersIcon size={12} className="text-slate-400"/>{r.contact_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.source === "itglue_import" ? (
                          <span className="text-slate-400 text-xs">—</span>
                        ) : (
                          <div className="flex items-center gap-2 justify-end">
                            <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div
                                className="h-full transition-all"
                                style={{
                                  width: `${r.section_progress_pct}%`,
                                  background: r.section_progress_pct >= 100 ? "#10b981" : r.section_progress_pct >= 50 ? "#f59e0b" : "#94a3b8",
                                }}
                              />
                            </div>
                            <span className="text-xs tabular-nums text-slate-600">{r.section_progress_pct}%</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1 text-xs">
                          {r.itglue_org_id && (
                            <a
                              href={r.itglue_org_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-brand-primary hover:underline"
                              data-testid={`itglue-link-${r.id}`}
                            >
                              <Cloud size={12}/>ITGlue #{r.itglue_org_id} <ExternalLink size={10}/>
                            </a>
                          )}
                          {r.onboarding_id && (
                            <span className="inline-flex items-center gap-1 text-slate-600">
                              <FileText size={12}/>Onboarding record
                            </span>
                          )}
                          {r.import_id && (
                            <span className="inline-flex items-center gap-1 text-slate-600">
                              <Database size={12}/>Import archive
                            </span>
                          )}
                          {r.file_count > 0 && (
                            <span className="inline-flex items-center gap-1 text-slate-500">
                              📎 {r.file_count} file(s)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(r.last_activity_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {r.onboarding_id && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => nav(`/admin/onboarding/${r.onboarding_id}`)} data-testid={`view-onb-${r.id}`} title="Open onboarding">
                                <Eye size={14}/>
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => nav(`/admin/onboarding/${r.onboarding_id}/edit`)} data-testid={`edit-onb-${r.id}`} title="Edit as admin">
                                <Pencil size={14}/>
                              </Button>
                            </>
                          )}
                          {r.import_id && (
                            <Button size="sm" variant="ghost" onClick={() => nav(`/admin/import-itglue`)} data-testid={`view-import-${r.id}`} title="Open import dashboard">
                              <Database size={14}/>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="mt-3 text-xs text-slate-500" data-testid="clients-footer">
          Showing {filtered.length} of {rows.length} clients{q && ` matching “${q}”`}.
        </div>
      </main>
      <BrandFooter/>
    </div>
  );
}

function StatCard({ label, value, tone = "slate", testId }) {
  const tones = {
    slate:   "bg-white border-slate-200 text-slate-700",
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    sky:     "bg-sky-50 border-sky-200 text-sky-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    violet:  "bg-violet-50 border-violet-200 text-violet-700",
  };
  return (
    <Card className={`p-4 border ${tones[tone] || tones.slate}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value ?? 0}</div>
    </Card>
  );
}

function SortHeader({ label, onClick, active, dir, testId, align = "left" }) {
  return (
    <th className={`px-4 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        onClick={onClick}
        data-testid={testId}
        className={`inline-flex items-center gap-1 hover:text-brand-primary transition ${active ? "text-brand-primary" : ""}`}
      >
        {label}
        <ArrowUpDown size={12} className={active ? "opacity-100" : "opacity-40"}/>
      </button>
    </th>
  );
}
